import { useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ReactComponent as EditIcon } from "../../asserts/logo/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../asserts/logo/delete.svg";
import { formatThresholdPercent } from "./sectionEditorUtils";
import { isFixedFrSection } from "./frElicitationSections";
import { configurationEditEnabled } from "../../config/productFeatures";

const C = {
  surface: "#FFFFFF",
  border: "#E6E6E6",
  primary: "#005BF0",
  headerBg: "#FFFFFF",
  text: "#383838",
  muted: "#707070",
  hover: "#F5F5F5",
};

const ACTIONS_COL_WIDTH = 120;
const TABLE_MIN_WIDTH = 1100;
const COLUMN_ORDER_STORAGE_KEY = "validaite_config_table_middle_column_order_v5";

export const MIDDLE_COLUMN_IDS = ["sno", "sectionName", "threshold", "items"];

const MIDDLE_COLUMN_META = {
  sno: { label: "S.No", align: "center", minWidth: 72 },
  sectionName: { label: "Section Name", align: "left", minWidth: 280 },
  threshold: { label: "Confidence threshold (%)", align: "center", minWidth: 200 },
  items: { label: "Items", align: "center", minWidth: 100 },
};

function loadMiddleColumnOrder() {
  try {
    const raw = window.localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    if (
      Array.isArray(parsed) &&
      parsed.length === MIDDLE_COLUMN_IDS.length &&
      MIDDLE_COLUMN_IDS.every((id) => parsed.includes(id))
    ) {
      return parsed;
    }
  } catch {
    /* ignore invalid storage */
  }
  return [...MIDDLE_COLUMN_IDS];
}

function saveMiddleColumnOrder(order) {
  try {
    window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(order));
  } catch {
    /* ignore quota / private mode */
  }
}

const headCellBaseSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: "0.5px",
  color: "#000000",
  borderBottom: `1px solid ${C.border}`,
  borderLeft: 0,
  borderRight: 0,
  py: 1.5,
  px: 2,
  bgcolor: C.headerBg,
  whiteSpace: "nowrap",
  verticalAlign: "middle",
};

const bodyCellBaseSx = {
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: "0.5px",
  color: C.text,
  borderBottom: `1px solid ${C.border}`,
  borderLeft: 0,
  borderRight: 0,
  py: 1.75,
  px: 2,
  verticalAlign: "middle",
  bgcolor: C.surface,
  ".MuiTableRow-root:hover &": { bgcolor: C.hover },
};

const stickyActionsHeadSx = {
  ...headCellBaseSx,
  position: "sticky",
  right: 0,
  zIndex: 4,
  width: ACTIONS_COL_WIDTH,
  minWidth: ACTIONS_COL_WIDTH,
  maxWidth: ACTIONS_COL_WIDTH,
  textAlign: "center",
};

const stickyActionsBodySx = {
  ...bodyCellBaseSx,
  position: "sticky",
  right: 0,
  zIndex: 3,
  width: ACTIONS_COL_WIDTH,
  minWidth: ACTIONS_COL_WIDTH,
  maxWidth: ACTIONS_COL_WIDTH,
  overflow: "visible",
  textAlign: "center",
};

const lockChipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "3px",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 11,
  lineHeight: "16px",
  letterSpacing: "0.3px",
  color: "#707070",
  bgcolor: "#F0F0F0",
  border: "1px solid #E0E0E0",
  borderRadius: "4px",
  px: "6px",
  py: "1px",
  cursor: "default",
  textTransform: "uppercase",
};

const sectionNameLinkSx = {
  border: 0,
  bgcolor: "transparent",
  p: 0,
  fontFamily: "Lato, sans-serif",
  fontWeight: 400,
  fontSize: 16,
  lineHeight: "24px",
  letterSpacing: "0.5px",
  color: C.primary,
  cursor: "pointer",
  textAlign: "left",
  "&:hover": { textDecoration: "underline" },
};

function actionTooltipSlotProps(width) {
  return {
    tooltip: {
      sx: {
        width,
        minWidth: width,
        height: 42,
        boxSizing: "border-box",
        bgcolor: "#000000",
        borderRadius: "4px",
        p: "12px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 400,
        fontSize: 12,
        lineHeight: "18px",
        letterSpacing: 0,
        color: "#E6E6E6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& .MuiTooltip-arrow": { color: "#000000" },
      },
    },
  };
}

function reorderMiddleColumns(order, fromKey, toKey) {
  if (!fromKey || !toKey || fromKey === toKey) return order;
  const next = [...order];
  const fromIndex = next.indexOf(fromKey);
  const toIndex = next.indexOf(toKey);
  if (fromIndex < 0 || toIndex < 0) return order;
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

function MiddleHeaderLabel({ columnId }) {
  if (columnId === "threshold") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <Box sx={{ textAlign: "center", lineHeight: "24px", whiteSpace: "nowrap" }}>
          {MIDDLE_COLUMN_META.threshold.label}
        </Box>
      </Box>
    );
  }
  return MIDDLE_COLUMN_META[columnId].label;
}

function RowActions({ idx, frOrTemplateLocked, canEditSection, onEdit, onView, onDelete }) {
  if (frOrTemplateLocked) {
    return (
      <Tooltip
        title="View (fixed — not editable)"
        arrow
        placement="bottom"
        enterDelay={0}
        componentsProps={actionTooltipSlotProps(120)}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onView(idx);
          }}
          aria-label="View section"
          sx={{ p: 0.75 }}
        >
          <VisibilityOutlinedIcon sx={{ width: 24, height: 24, color: C.muted }} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <>
      {canEditSection ? (
        <Tooltip
          title="Edit"
          arrow
          placement="bottom"
          enterDelay={0}
          componentsProps={actionTooltipSlotProps(47)}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(idx);
            }}
            aria-label="Edit section"
            sx={{ p: 0.75 }}
          >
            <EditIcon style={{ width: 24, height: 24, display: "block" }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip
          title="View"
          arrow
          placement="bottom"
          enterDelay={0}
          componentsProps={actionTooltipSlotProps(47)}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onView(idx);
            }}
            aria-label="View section"
            sx={{ p: 0.75 }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 22, color: C.muted }} />
          </IconButton>
        </Tooltip>
      )}
      {configurationEditEnabled() && (
        <Tooltip
          title="Delete"
          arrow
          placement="bottom"
          enterDelay={0}
          componentsProps={actionTooltipSlotProps(60)}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(idx);
            }}
            aria-label="Delete section"
            sx={{ p: 0.75, ml: 0.5 }}
          >
            <DeleteIcon style={{ width: 24, height: 24, display: "block" }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
}

export default function ConfigurationTable({
  sections,
  structureLocked,
  editEnabled,
  onEdit,
  onView,
  onDelete,
}) {
  const [middleOrder, setMiddleOrder] = useState(loadMiddleColumnOrder);
  const [draggingKey, setDraggingKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  const applyOrder = (next) => {
    setMiddleOrder(next);
    saveMiddleColumnOrder(next);
  };

  const handleDragStart = (event, columnId) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", columnId);
    setDraggingKey(columnId);
  };

  const handleDragOver = (event, columnId) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (overKey !== columnId) setOverKey(columnId);
  };

  const handleDrop = (event, columnId) => {
    event.preventDefault();
    const fromKey = event.dataTransfer.getData("text/plain") || draggingKey;
    applyOrder(reorderMiddleColumns(middleOrder, fromKey, columnId));
    setDraggingKey(null);
    setOverKey(null);
  };

  const handleDragEnd = () => {
    setDraggingKey(null);
    setOverKey(null);
  };

  const renderMiddleCell = (columnId, section, idx, canEditSection, frOrTemplateLocked) => {
    const meta = MIDDLE_COLUMN_META[columnId];
    if (columnId === "sno") {
      return (
        <TableCell key={columnId} align="center" sx={{ ...bodyCellBaseSx, minWidth: meta.minWidth }}>
          {idx + 1}
        </TableCell>
      );
    }
    if (columnId === "sectionName") {
      return (
        <TableCell key={columnId} align={meta.align} sx={{ ...bodyCellBaseSx, minWidth: meta.minWidth }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                canEditSection ? onEdit(idx) : onView(idx);
              }}
              sx={sectionNameLinkSx}
            >
              {section.name}
            </Typography>
            {frOrTemplateLocked && (
              <Tooltip
                title="Fixed by ValidAIte to capture functional requirements — cannot be edited or removed. You'll answer these in the chat."
                arrow
                placement="top"
              >
                <Box component="span" sx={lockChipSx}>
                  <LockOutlinedIcon sx={{ fontSize: 12 }} />
                  Fixed
                </Box>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      );
    }
    if (columnId === "threshold") {
      return (
        <TableCell
          key={columnId}
          align="center"
          sx={{ ...bodyCellBaseSx, minWidth: meta.minWidth, fontVariantNumeric: "tabular-nums" }}
        >
          {formatThresholdPercent(section.threshold)}
        </TableCell>
      );
    }
    return (
      <TableCell key={columnId} align="center" sx={{ ...bodyCellBaseSx, minWidth: meta.minWidth }}>
        {section.items?.length || section.items_count || 0}
      </TableCell>
    );
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: `1px solid ${C.border}`,
        borderRadius: "4px",
        bgcolor: C.surface,
        overflowX: "auto",
        maxWidth: "100%",
        scrollbarWidth: "thin",
        scrollbarColor: "#EAECF0 transparent",
        "&::-webkit-scrollbar": { height: 6 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#EAECF0",
          borderRadius: "12px",
        },
      }}
    >
      <Table
        size="medium"
        aria-label="Configuration sections"
        sx={{
          minWidth: TABLE_MIN_WIDTH,
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <TableHead>
          <TableRow sx={{ bgcolor: C.headerBg }}>
            {middleOrder.map((columnId) => {
              const meta = MIDDLE_COLUMN_META[columnId];
              const isOver = overKey === columnId && draggingKey && draggingKey !== columnId;
              return (
                <TableCell
                  key={columnId}
                  align={meta.align}
                  draggable
                  onDragStart={(event) => handleDragStart(event, columnId)}
                  onDragOver={(event) => handleDragOver(event, columnId)}
                  onDrop={(event) => handleDrop(event, columnId)}
                  onDragEnd={handleDragEnd}
                  title="Drag to reorder column"
                  sx={{
                    ...headCellBaseSx,
                    minWidth: meta.minWidth,
                    cursor: "grab",
                    opacity: draggingKey === columnId ? 0.45 : 1,
                    boxShadow: isOver ? `inset 0 -3px 0 ${C.primary}` : "none",
                    userSelect: "none",
                  }}
                >
                  <MiddleHeaderLabel columnId={columnId} />
                </TableCell>
              );
            })}
            <TableCell sx={stickyActionsHeadSx}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sections.map((section, idx) => {
            const rowId = section.id || `row-${idx}`;
            const frOrTemplateLocked = isFixedFrSection(section) || structureLocked;
            const canEditSection = editEnabled && !isFixedFrSection(section);
            return (
              <TableRow
                key={`${rowId}-${idx}`}
                hover
                onClick={() => (canEditSection ? onEdit(idx) : onView(idx))}
                sx={{
                  cursor: "pointer",
                  "&:last-child td": { borderBottom: 0 },
                  "&.MuiTableRow-hover:hover": { bgcolor: C.hover },
                }}
              >
                {middleOrder.map((columnId) =>
                  renderMiddleCell(columnId, section, idx, canEditSection, frOrTemplateLocked)
                )}
                <TableCell sx={stickyActionsBodySx} onClick={(e) => e.stopPropagation()}>
                  <RowActions
                    idx={idx}
                    frOrTemplateLocked={frOrTemplateLocked}
                    canEditSection={canEditSection}
                    onEdit={onEdit}
                    onView={onView}
                    onDelete={onDelete}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export { reorderMiddleColumns };
